import { useState } from 'react';
import { Sheet } from '../../components/Overlay.tsx';
import { Seg } from '../../components/Seg.tsx';
import {
  ALL,
  CATEGORIES,
  MAX_REPRESENTATIVE,
  NO_FILTER,
  PRAYER_STATUS,
  PRIORITY,
  SORT_KEYS,
  nextSort,
  priorityLabel,
  sortLabel,
  statusLabel,
} from '../../domain.ts';
import type { Filter, Sort } from '../../domain.ts';

/**
 * C1-4 — 필터 (바텀시트).
 *
 * 바깥 버튼이 지름길이라면 여기는 전부입니다. 두 곳은 같은 상태를 봅니다 —
 * 여기서 바꾸면 바깥 버튼도 따라 켜지고 꺼집니다.
 *
 * 닫는 길은 셋입니다. **적용으로 닫으면 고른 것이 걸리고, 손잡이를 끌거나
 * 어두운 배경을 눌러 닫으면 고르기 전으로 되돌아갑니다.** 그래서 여기서
 * 고르는 동안은 초안을 따로 들고 있다가 적용할 때 한 번에 넘깁니다 —
 * 바깥 버튼처럼 즉시 반영하면 «취소하고 닫기»가 성립하지 않습니다.
 */

interface Props {
  sort: Sort;
  filter: Filter;
  onApply: (sort: Sort, filter: Filter) => void;
  onClose: () => void;
}

export function FilterSheet({ sort, filter, onApply, onClose }: Props) {
  const [draftSort, setDraftSort] = useState(sort);
  const [draft, setDraft] = useState(filter);

  return (
    <Sheet
      title="필터"
      // 각 줄의 «전체» 가 그 조건을 쓰지 않는 상태이고, 초기화는 모든 줄을 한 번에 되돌립니다
      action={{
        label: '초기화',
        onClick: () => {
          setDraft(NO_FILTER);
          setDraftSort({ key: 'recent', dir: 'down' });
        },
      }}
      onClose={onClose}
    >
      {/* 항목이 시트보다 길면 시트를 늘리지 않고 여기서 스크롤합니다.
          적용 버튼은 바닥에 붙어 있어 스크롤이 어디에 있든 누를 수 있습니다 */}
      <div className="sheet__scroll">
        <div className="field">
          <span className="field__label">정렬</span>
          <div className="seg" role="group" aria-label="정렬">
            {SORT_KEYS.map((key) => {
              const on = key === draftSort.key;
              return (
                <button
                  key={key}
                  type="button"
                  className={on ? 'sg sg--on' : 'sg'}
                  aria-pressed={on}
                  onClick={() => setDraftSort(nextSort(draftSort, key))}
                >
                  {sortLabel[key]}
                  {on && (draftSort.dir === 'down' ? ' ↓' : ' ↑')}
                </button>
              );
            })}
          </div>
        </div>

        <div className="field">
          <span className="field__label">영역</span>
          <Seg
            label="영역"
            options={[
              { value: ALL, label: '전체' },
              ...CATEGORIES.map((c) => ({ value: c, label: c })),
            ]}
            value={draft.category}
            onChange={(category) => setDraft({ ...draft, category })}
          />
        </div>

        <div className="field">
          <span className="field__label">기도 상태</span>
          <Seg
            label="기도 상태"
            options={[
              { value: ALL, label: '전체' },
              ...PRAYER_STATUS.map((s) => ({ value: s, label: statusLabel[s] })),
            ]}
            value={draft.status}
            onChange={(status) => setDraft({ ...draft, status })}
          />
        </div>

        <div className="field">
          <span className="field__label">중요도</span>
          <Seg
            label="중요도"
            options={[
              { value: ALL, label: '전체' },
              ...PRIORITY.map((p) => ({ value: p, label: priorityLabel[p] })),
            ]}
            value={draft.priority}
            crit="URGENT"
            onChange={(priority) => setDraft({ ...draft, priority })}
          />
        </div>

        <div className="field">
          <span className="field__label">대표 기도제목</span>
          <button
            type="button"
            className="toggle-row"
            aria-pressed={draft.representativeOnly}
            onClick={() => setDraft({ ...draft, representativeOnly: !draft.representativeOnly })}
          >
            <span className="toggle-row__label">
              대표만 보기
              <span className="toggle-row__sub">
                대표로 지정한 {MAX_REPRESENTATIVE}개만
              </span>
            </span>
            <span className={draft.representativeOnly ? 'toggle toggle--on' : 'toggle'} />
          </button>
        </div>
      </div>

      <button
        type="button"
        className="btn btn--primary"
        onClick={() => onApply(draftSort, draft)}
      >
        적용
      </button>
    </Sheet>
  );
}
